<template>
  <button v-if="!syncJobId" type="button" class="btn btn-sm btn-light" 
    @click="syncStart"
    v-tooltip
    data-bs-toggle="tooltip" 
    title="Network Refresh/Sync"
  >
    <img type="png" src="@/assets/icons/netsync.png" alt="Network sync" style="height: 20px;" />
  </button>
  
  <!-- Micro Progress Bar Tracker Container -->
  <div v-else class="d-flex align-items-center gap-2">
    <!-- FIX 1: Removed the stray comma artifact that was at the end of this line -->
    <div class="d-flex flex-column justify-content-center" style="width: 140px; height: 31px;">
      <!-- Small text tracker status indicator -->
      <span class="text-muted text-center mb-1" style="font-size: 0.65rem; line-height: 1;">
        Syncing... {{ syncProgress }}%
      </span>
      
      <!-- Core Bootstrap Progress Track -->
      <!-- FIX 2: Added w-100 and layout styling onto the parent track container -->
      <div class="progress w-100" style="height: 6px; position: relative;">
        <div 
          class="progress-bar progress-bar-striped progress-bar-animated bg-success" 
          role="progressbar" 
          :style="{ width: `${syncProgress}%` }" 
          :aria-valuenow="syncProgress" 
          aria-valuemin="0" 
          aria-valuemax="100"
        ></div>
      </div>
    </div>
    <button 
      class="btn btn-sm btn-close"
      style="font-size: 0.65rem;" 
      v-tooltip title="Cancel"  
      @click="syncCancel(); $event.target.blur();"
    />
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref } from 'vue';
import { apiFetch } from '@/utils/api';

const emit = defineEmits(['syncStart', 'syncCancel', 'syncComplete']);

const props = defineProps({
  endpoint: String,
  refreshSeconds: { type: Number, default: 10 },
});

const syncJobId = ref(null);
const syncProgress = ref(0);

const syncComplete = (status) => {
  return ['COMPLETED', 'FAILED', 'CANCELLED'].includes(status);
};

let pollingInterval = null;

const syncStart = async () => {
  const response = await apiFetch(`${props.endpoint}/sync`, {
    method: 'POST',
    body: {},
  });
  syncProgress.value = 0;
  syncJobId.value = response.jobId;
  syncStartPolling();
  emit('syncStart');
};

const syncCancel = async () => {
  const url = `${props.endpoint}/sync/cancel?jobId=${syncJobId.value}`;
  const response = await apiFetch(url, { method: 'POST', body: {} });
  const { success, message } = response;
  console.log(
    `Cancelled SIM sync job ${syncJobId.value}${message ? (':' + message) : ''}`
  );
  if (success) {
    await syncStopPolling();
  }
  emit('syncCancel');
};

const syncStartPolling = async () => {
  if (pollingInterval) clearInterval(pollingInterval);
  pollingInterval = setInterval(async () => {
    try {
      const url = `${props.endpoint}/sync/status?jobId=${syncJobId.value}`;
      const response = await apiFetch(url); 
      const { id, status, total = 0, processed = 0, failed = 0 } = response;
      const updated = total > 0 ? Math.round((processed / total) * 100) : 0;
      if (updated !== syncProgress.value) {
        console.debug(`Progress updated to ${updated}%`);
      }
      syncProgress.value = total > 0 ? Math.round((processed / total) * 100) : 0;
      if (syncComplete(status) || syncProgress.value >= 100) {
        syncStopPolling();
      }
    } catch (error) {
      console.error("Failed to fetch sync metrics:", error);
      syncStopPolling();
    }
  }, props.refreshSeconds * 1000);
};

const syncStopPolling = async () => {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
  // Short UI delay so the user physically sees the track reach 100% before flashing away
  setTimeout(() => {
    syncJobId.value = null;
    syncProgress.value = 0;
    emit('syncComplete');
  }, 600);
};

const unpoll = () => {
  if (pollingInterval) clearInterval(pollingInterval);
  pollingInterval = null;
};

onMounted(unpoll);

onBeforeUnmount(unpoll);

</script>